/**
 * Session recovery and parameter persistence utilities
 */
import { clearEmpiricalStudySession } from './empiricalStudySession';

export interface StoredSessionData {
  sessionId: string;
  playerId: string;
  gameCode: string;
  playerName: string;
  characterIndex: number;
  joinedAt: Date;
  lastActivity: Date;
}

export interface SessionRecoveryState {
  hasStoredSession: boolean;
  storedData: StoredSessionData | null;
  canRecover: boolean;
  recoverySource: 'none' | 'sessionStorage' | 'localStorage';
}

const SESSION_STORAGE_KEY = 'homerun_student_session';
const LOCAL_STORAGE_KEY = 'homerun_student_session_backup';
const EXIT_STATE_KEY = 'homerun_exit_in_progress';
const SESSION_RECOVERY_TTL_MS = 1000 * 60 * 60 * 4;
const RECOVERABLE_ROUTE_PREFIXES = [
  '/countdown',
  '/question',
  '/game',
  '/scores',
  '/bonus',
  '/final-scores'
] as const;

const parseStoredDate = (value: unknown): Date | null => {
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (Number.isFinite(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
};

const normalizeStoredSessionData = (value: unknown): StoredSessionData | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Partial<StoredSessionData> & Record<string, unknown>;
  const joinedAt = parseStoredDate(candidate.joinedAt);
  const lastActivity = parseStoredDate(candidate.lastActivity);

  if (
    typeof candidate.sessionId !== 'string' ||
    typeof candidate.playerId !== 'string' ||
    typeof candidate.gameCode !== 'string' ||
    typeof candidate.playerName !== 'string' ||
    !joinedAt ||
    !lastActivity
  ) {
    return null;
  }

  return {
    sessionId: candidate.sessionId,
    playerId: candidate.playerId,
    gameCode: candidate.gameCode,
    playerName: candidate.playerName,
    characterIndex: typeof candidate.characterIndex === 'number' && Number.isFinite(candidate.characterIndex)
      ? candidate.characterIndex
      : 0,
    joinedAt,
    lastActivity
  };
};

const isStoredSessionFresh = (data: StoredSessionData): boolean => {
  return Date.now() - data.lastActivity.getTime() <= SESSION_RECOVERY_TTL_MS;
};

const clearStorageKey = (storage: Storage, key: string): void => {
  try {
    storage.removeItem(key);
  } catch (error) {
    console.error(`❌ Failed to clear storage key ${key}:`, error);
  }
};

const readStoredSessionData = (
  storage: Storage,
  key: string,
  source: SessionRecoveryState['recoverySource']
): SessionRecoveryState | null => {
  const rawValue = storage.getItem(key);
  if (!rawValue) {
    return null;
  }

  try {
    const normalizedData = normalizeStoredSessionData(JSON.parse(rawValue));
    if (!normalizedData) {
      console.warn(`⚠️ Ignoring invalid ${source} session recovery payload`);
      clearStorageKey(storage, key);
      return null;
    }

    if (!isStoredSessionFresh(normalizedData)) {
      console.log(`🧹 Clearing stale ${source} session recovery payload`);
      clearStorageKey(storage, key);
      return null;
    }

    console.log(`🔄 Found session data in ${source}:`, normalizedData);
    return {
      hasStoredSession: true,
      storedData: normalizedData,
      canRecover: Boolean(normalizedData.sessionId && normalizedData.playerId),
      recoverySource: source
    };
  } catch (error) {
    console.error(`❌ Failed to parse ${source} session data:`, error);
    clearStorageKey(storage, key);
    return null;
  }
};

/**
 * Exit state management to prevent recovery during intentional exits
 */
export const setExitInProgress = (): void => {
  try {
    sessionStorage.setItem(EXIT_STATE_KEY, 'true');
    console.log('🚪 Exit state set - recovery disabled');
  } catch (error) {
    console.error('❌ Failed to set exit state:', error);
  }
};

export const clearExitState = (): void => {
  try {
    sessionStorage.removeItem(EXIT_STATE_KEY);
    console.log('✅ Exit state cleared');
  } catch (error) {
    console.error('❌ Failed to clear exit state:', error);
  }
};

export const isExitInProgress = (): boolean => {
  try {
    return sessionStorage.getItem(EXIT_STATE_KEY) === 'true';
  } catch (error) {
    console.error('❌ Failed to check exit state:', error);
    return false;
  }
};

/**
 * Store session data for recovery
 */
export const storeSessionData = (data: Partial<StoredSessionData>): void => {
  try {
    const timestamp = new Date();
    const sessionData: StoredSessionData = {
      sessionId: data.sessionId || '',
      playerId: data.playerId || '',
      gameCode: data.gameCode || '',
      playerName: data.playerName || '',
      characterIndex: data.characterIndex || 0,
      joinedAt: data.joinedAt || timestamp,
      lastActivity: timestamp
    };

    // Store in both session and local storage for redundancy
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(sessionData));

    console.log('💾 Session data stored:', sessionData);
  } catch (error) {
    console.error('❌ Failed to store session data:', error);
  }
};

/**
 * Retrieve stored session data
 */
export const getStoredSessionData = (): SessionRecoveryState => {
  try {
    const sessionState = readStoredSessionData(sessionStorage, SESSION_STORAGE_KEY, 'sessionStorage');
    if (sessionState) {
      return sessionState;
    }

    const localState = readStoredSessionData(localStorage, LOCAL_STORAGE_KEY, 'localStorage');
    if (localState) {
      return localState;
    }

    return {
      hasStoredSession: false,
      storedData: null,
      canRecover: false,
      recoverySource: 'none'
    };
  } catch (error) {
    console.error('❌ Failed to retrieve session data:', error);
    return {
      hasStoredSession: false,
      storedData: null,
      canRecover: false,
      recoverySource: 'none'
    };
  }
};

/**
 * Update last activity timestamp
 */
export const updateSessionActivity = (): void => {
  try {
    const recoveryState = getStoredSessionData();
    if (recoveryState.storedData) {
      const updatedData = {
        ...recoveryState.storedData,
        lastActivity: new Date()
      };
      storeSessionData(updatedData);
    }
  } catch (error) {
    console.error('❌ Failed to update session activity:', error);
  }
};

/**
 * Clear stored session data
 */
export const clearStoredSessionData = (): void => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    sessionStorage.removeItem(EXIT_STATE_KEY);
    clearEmpiricalStudySession();
    console.log('🗑️ Session data and exit state cleared');
  } catch (error) {
    console.error('❌ Failed to clear session data:', error);
  }
};

export const shouldAttemptSessionRecovery = (
  pathname: string = window.location.pathname
): boolean => {
  return RECOVERABLE_ROUTE_PREFIXES.some(route =>
    pathname === route || pathname.startsWith(`${route}/`)
  );
};

export const getDocumentNavigationType = (): string | null => {
  if (typeof window === 'undefined' || typeof performance === 'undefined') {
    return null;
  }

  const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  const navigationEntry = navigationEntries[0];

  if (navigationEntry?.type) {
    return navigationEntry.type;
  }

  const legacyNavigation = performance.navigation;
  if (!legacyNavigation || typeof legacyNavigation.type !== 'number') {
    return null;
  }

  switch (legacyNavigation.type) {
    case legacyNavigation.TYPE_NAVIGATE:
      return 'navigate';
    case legacyNavigation.TYPE_RELOAD:
      return 'reload';
    case legacyNavigation.TYPE_BACK_FORWARD:
      return 'back_forward';
    default:
      return null;
  }
};

export const shouldResetFreshStudentEntry = (
  pathname: string = window.location.pathname,
  options?: { spectator?: boolean }
): boolean => {
  if (options?.spectator) {
    return false;
  }

  if (!shouldAttemptSessionRecovery(pathname)) {
    return false;
  }

  const navigationType = getDocumentNavigationType();
  return navigationType === 'navigate' || navigationType === 'back_forward';
};

/**
 * Restore URL parameters from stored session data
 */
export const restoreUrlFromSession = (storedData: StoredSessionData): string => {
  const params = new URLSearchParams();
  params.set('sessionId', storedData.sessionId);
  params.set('playerId', storedData.playerId);

  const restoredUrl = `/join-game/${storedData.gameCode}?${params.toString()}`;
  console.log('🔗 Restored URL from session:', restoredUrl);
  return restoredUrl;
};

/**
 * Check if current session matches stored session
 */
export const validateSessionConsistency = (
  currentSessionId: string | null,
  currentPlayerId: string | null
): {
  isConsistent: boolean;
  shouldRecover: boolean;
  storedData: StoredSessionData | null;
} => {
  const recoveryState = getStoredSessionData();

  if (!recoveryState.storedData) {
    return {
      isConsistent: true, // No stored data to compare against
      shouldRecover: false,
      storedData: null
    };
  }

  const isConsistent =
    currentSessionId === recoveryState.storedData.sessionId &&
    currentPlayerId === recoveryState.storedData.playerId;

  const shouldRecover =
    !currentSessionId &&
    !currentPlayerId &&
    recoveryState.canRecover;

  console.log('🔍 Session consistency check:', {
    current: { sessionId: currentSessionId, playerId: currentPlayerId },
    stored: { sessionId: recoveryState.storedData.sessionId, playerId: recoveryState.storedData.playerId },
    isConsistent,
    shouldRecover
  });

  return {
    isConsistent,
    shouldRecover,
    storedData: recoveryState.storedData
  };
};

/**
 * Auto-recovery hook for URL parameters
 */
export const useSessionRecovery = () => {
  const recover = (storedData: StoredSessionData) => {
    try {
      const restoredUrl = restoreUrlFromSession(storedData);
      console.log('🔄 Attempting session recovery, navigating to:', restoredUrl);

      // Use history.replaceState to restore URL without navigation
      window.history.replaceState({}, '', restoredUrl);

      // Force a page refresh to ensure all components see the new URL
      window.location.reload();
    } catch (error) {
      console.error('❌ Session recovery failed:', error);
    }
  };

  const checkAndRecover = (currentSessionId: string | null, currentPlayerId: string | null) => {
    // Don't attempt recovery if user is in the middle of exiting
    if (isExitInProgress()) {
      console.log('🚪 Exit in progress - skipping session recovery');
      return false;
    }

    const validation = validateSessionConsistency(currentSessionId, currentPlayerId);

    if (validation.shouldRecover && validation.storedData) {
      console.log('🔄 Auto-recovering session from stored data');
      recover(validation.storedData);
      return true;
    }

    return false;
  };

  return {
    recover,
    checkAndRecover,
    getStoredSessionData,
    storeSessionData,
    clearStoredSessionData,
    validateSessionConsistency,
    setExitInProgress,
    clearExitState,
    isExitInProgress
  };
};
